const digitsCache = new Map<string, number>()
const formatterCache = new Map<string, Intl.NumberFormat>()

export const currencyDigits = (currency: string): number => {
    const cached = digitsCache.get(currency)
    if (cached !== undefined) return cached

    let digits = 2

    try {
        digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2
    }
    catch {
        digits = 2
    }

    digitsCache.set(currency, digits)
    return digits
}

const getFormatter = (currency: string): Intl.NumberFormat => {
    const cached = formatterCache.get(currency)
    if (cached) return cached

    const digits = currencyDigits(currency)
    let formatter: Intl.NumberFormat

    try {
        formatter = new Intl.NumberFormat('en', {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits
        })
    }
    catch {
        formatter = new Intl.NumberFormat('en', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })
    }

    formatterCache.set(currency, formatter)
    return formatter
}

/** Formats an amount stored in minor units (cents) for display. */
export const formatMoney = (minor: number, currency: string): string => {
    const digits = currencyDigits(currency)
    return getFormatter(currency).format(minor / 10 ** digits)
}

/**
 * Converts an amount in one currency's minor units into another's, using a rate
 * of "1 unit of `from` = `rate` units of `to`". Shared by the browser preview and the server.
 */
export const convertMinor = (minor: number, from: string, to: string, rate: number): number => {
    if (from === to) return minor

    const fromDigits = currencyDigits(from)
    const toDigits = currencyDigits(to)

    return Math.round((minor / 10 ** fromDigits) * rate * 10 ** toDigits)
}

/** A readable rate, e.g. 0.0056513 -> "0.005651", 1.0834 -> "1.0834". */
export const formatRate = (rate: number): string => {
    if (!Number.isFinite(rate) || rate <= 0) return ''
    if (rate >= 100) return rate.toFixed(2)
    if (rate >= 1) return rate.toFixed(4)

    return rate.toPrecision(4)
}

/** Formats minor units as a plain number for an input field, e.g. 1250 -> "12.50". */
export const minorToInput = (minor: number, currency: string): string => {
    const digits = currencyDigits(currency)
    if (digits === 0) return String(minor)

    const whole = Math.floor(minor / 10 ** digits)
    const fraction = String(minor % 10 ** digits).padStart(digits, '0')

    return `${whole}.${fraction}`
}

/**
 * Parses what someone typed ("12.50", "12,50", "1,234.5", "1.234,50") into minor units.
 * Returns null when it is not a valid, non-negative amount.
 */
export const parseAmount = (input: string, currency: string): number | null => {
    const digits = currencyDigits(currency)
    const cleaned = input.trim().replace(/[\s' ]/g, '').replace(/^[^\d.,]+/, '')

    if (!cleaned || !/^[\d.,]+$/.test(cleaned)) return null

    let whole = cleaned
    let fraction = ''

    const lastComma = cleaned.lastIndexOf(',')
    const lastDot = cleaned.lastIndexOf('.')
    const lastSeparator = Math.max(lastComma, lastDot)

    if (lastSeparator !== -1) {
        const tail = cleaned.slice(lastSeparator + 1)
        const hasBoth = lastComma !== -1 && lastDot !== -1

        // Currencies without minor units only accept thousands separators.
        if (digits === 0 && tail.length !== 3) return null
        const isDecimal = digits > 0 && (hasBoth || tail.length <= digits) && tail.length > 0

        if (isDecimal) {
            whole = cleaned.slice(0, lastSeparator)
            fraction = tail
        }
    }

    whole = whole.replace(/[.,]/g, '')

    if (!/^\d*$/.test(whole) || !/^\d*$/.test(fraction)) return null
    if (fraction.length > digits) return null
    if (!whole && !fraction) return null

    const wholeValue = Number(whole || '0')
    const fractionValue = Number((fraction || '').padEnd(digits, '0') || '0')
    const result = wholeValue * 10 ** digits + fractionValue

    if (!Number.isSafeInteger(result)) return null

    return result
}
